/**
 * ProgressiveSignupForm
 *
 * 3-step signup sheet (Dialog on desktop, bottom-anchored on mobile).
 * Adapts its fields based on the user's segment (founder | investor).
 *
 * Step 1, Email capture only ("See opportunities, takes 30 sec")
 * Step 2, Basic info (role-specific)
 * Step 3, Profile completion teaser with progress bar + skip option
 *
 * On final submit:
 *   1. Inserts a row into signup_leads (unauthenticated, public insert)
 *   2. Calls the conversion-ai edge function to generate a welcome email
 *   3. Redirects to app.adv3nture.io/signup?email=...&segment=... so the
 *      user doesn't have to retype their email on the app's own signup screen.
 *
 * Props:
 *   segment  , "founder" | "investor" (from SegmentSelector)
 *   open     , controlled open state
 *   onClose(), called when form is dismissed
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import type { Segment } from "@/components/SegmentSelector";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FormData {
  email: string;
  fullName: string;
  company: string;
  stage: string;        // founders only
  focusAreas: string;  // investors only (comma-separated)
}

interface ProgressiveSignupFormProps {
  segment: Segment;
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FOUNDER_STAGES = ["Pre-idea", "Pre-seed", "Seed", "Series A", "Series B+"];

const INVESTOR_FOCUS = [
  "SaaS", "FinTech", "HealthTech", "CleanTech", "EdTech",
  "DeepTech", "Consumer", "Marketplace", "Enterprise", "Web3",
];

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

const StepDots = ({ current, total }: { current: number; total: number }) => (
  <div className="flex items-center gap-1.5" aria-label={`Step ${current} of ${total}`}>
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={`block rounded-full transition-all duration-300 ${
          i < current
            ? "w-4 h-1.5 bg-primary"
            : i === current
            ? "w-4 h-1.5 bg-primary/60"
            : "w-1.5 h-1.5 bg-muted-foreground/30"
        }`}
      />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ProgressiveSignupForm = ({ segment, open, onClose }: ProgressiveSignupFormProps) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>({
    email: "",
    fullName: "",
    company: "",
    stage: "",
    focusAreas: "",
  });
  const [selectedFocus, setSelectedFocus] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isFounder = segment === "founder";

  const set = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  // ── Step 0: email ────────────────────────────────────────────────────────

  const handleStep0 = (e: React.FormEvent) => {
    e.preventDefault();
    const email = form.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(null);
    setStep(1);
  };

  // ── Step 1: basic info ───────────────────────────────────────────────────

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.company.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    if (isFounder && !form.stage) {
      setError("Please select your funding stage.");
      return;
    }
    if (!isFounder && selectedFocus.length === 0) {
      setError("Please select at least one focus area.");
      return;
    }
    setError(null);
    setStep(2);
  };

  // ── Step 2: submit ───────────────────────────────────────────────────────

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Insert into signup_leads (public table, no auth required)
      const { error: insertError } = await supabase
        .from("signup_leads" as never)
        .insert({
          email: form.email.trim().toLowerCase(),
          full_name: form.fullName.trim(),
          company: form.company.trim(),
          segment,
          stage: isFounder ? form.stage : null,
          focus_areas: !isFounder ? selectedFocus : null,
          source: "marketing_site",
        } as never);

      if (insertError && insertError.code !== "23505") {
        // 23505 = unique violation (already signed up), treat as success
        throw insertError;
      }

      // Trigger welcome email via edge function (fire-and-forget)
      supabase.functions
        .invoke("conversion-ai", {
          body: {
            action: "welcome-email",
            payload: {
              userName: form.fullName.trim().split(" ")[0],
              segment,
            },
          },
        })
        .then(({ data }) => {
          if (data?.subject && data?.body) {
            // Log for now, wire to your email sender when ready
            console.info("Welcome email generated:", data.subject);
          }
        })
        .catch((err) => console.warn("Welcome email generation failed:", err));

      setSuccess(true);

      // Redirect to the app with email + segment pre-filled so the user
      // doesn't have to retype their email on the app's signup screen.
      setTimeout(() => {
        const params = new URLSearchParams({
          email: form.email.trim().toLowerCase(),
          segment,
          ...(isFounder && form.stage ? { stage: form.stage } : {}),
        });
        window.location.href = `https://app.adv3nture.io/signup?${params.toString()}`;
      }, 1800);
    } catch (err) {
      console.error("Signup error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle focus area chip ────────────────────────────────────────────────

  const toggleFocus = (area: string) => {
    setSelectedFocus((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
  };

  // ── Reset on close ───────────────────────────────────────────────────────

  const handleClose = () => {
    if (loading) return;
    setStep(0);
    setError(null);
    setSuccess(false);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ type: "spring", stiffness: 280, damping: 28 }}
        className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="signup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="glass rounded-3xl p-8 w-full max-w-md relative shadow-2xl">
          {/* Close */}
          <button
            onClick={handleClose}
            aria-label="Close signup form"
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Step indicator */}
          <div className="flex items-center justify-between mb-6">
            <StepDots current={step} total={3} />
            <span className="text-xs text-muted-foreground">
              {step === 0 ? "Step 1 of 3" : step === 1 ? "Step 2 of 3" : "Almost done"}
            </span>
          </div>

          {/* ── Success state ── */}
          {success && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-display font-bold mb-2">You're in!</h2>
              <p className="text-muted-foreground text-sm">
                Opening AdV3nture for you now…
              </p>
            </motion.div>
          )}

          {/* ── Step 0: Email ── */}
          {!success && step === 0 && (
            <AnimatePresence mode="wait">
              <motion.form
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleStep0}
                className="space-y-5"
              >
                <div>
                  <h2 id="signup-title" className="text-2xl font-display font-bold mb-1">
                    {isFounder
                      ? "Discover investors seeking founders like you"
                      : "Explore founders raising in your focus areas"}
                  </h2>
                  <p className="text-muted-foreground text-sm">Free to join. Takes 30 seconds.</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={set("email")}
                    autoFocus
                    required
                  />
                </div>

                {error && <p className="text-destructive text-sm">{error}</p>}

                <Button type="submit" size="lg" className="w-full glow-primary gap-2">
                  See opportunities <ArrowRight className="w-4 h-4" />
                </Button>

                <p className="text-center text-xs text-muted-foreground">
                  No spam. Unsubscribe any time.
                </p>
              </motion.form>
            </AnimatePresence>
          )}

          {/* ── Step 1: Basic info ── */}
          {!success && step === 1 && (
            <AnimatePresence mode="wait">
              <motion.form
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleStep1}
                className="space-y-5"
              >
                <div>
                  <h2 id="signup-title" className="text-xl font-display font-bold mb-1">
                    Tell us a bit about yourself
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    This helps surface the right opportunities for you.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="fullName">Full name</Label>
                  <Input
                    id="fullName"
                    placeholder="Alex Johnson"
                    value={form.fullName}
                    onChange={set("fullName")}
                    autoFocus
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="company">
                    {isFounder ? "Company / startup name" : "Fund or firm name"}
                  </Label>
                  <Input
                    id="company"
                    placeholder={isFounder ? "Acme Labs" : "Horizon Ventures"}
                    value={form.company}
                    onChange={set("company")}
                    required
                  />
                </div>

                {/* Founder: stage selector */}
                {isFounder && (
                  <div className="space-y-2">
                    <Label>Funding stage</Label>
                    <div className="flex flex-wrap gap-2">
                      {FOUNDER_STAGES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, stage: s }))}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            form.stage === s
                              ? "border-primary bg-primary/20 text-primary"
                              : "border-border text-muted-foreground hover:border-primary/50"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Investor: focus area chips */}
                {!isFounder && (
                  <div className="space-y-2">
                    <Label>Focus areas (select all that apply)</Label>
                    <div className="flex flex-wrap gap-2">
                      {INVESTOR_FOCUS.map((area) => (
                        <button
                          key={area}
                          type="button"
                          onClick={() => toggleFocus(area)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                            selectedFocus.includes(area)
                              ? "border-secondary bg-secondary/20 text-secondary"
                              : "border-border text-muted-foreground hover:border-secondary/50"
                          }`}
                        >
                          {area}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {error && <p className="text-destructive text-sm">{error}</p>}

                <Button type="submit" size="lg" className="w-full glow-primary gap-2">
                  Continue <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.form>
            </AnimatePresence>
          )}

          {/* ── Step 2: Profile teaser ── */}
          {!success && step === 2 && (
            <AnimatePresence mode="wait">
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5"
              >
                <div>
                  <h2 id="signup-title" className="text-xl font-display font-bold mb-1">
                    Your profile is 40% complete
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {isFounder
                      ? "Complete your profile to get discovered by more investors."
                      : "Complete your profile to surface better opportunities."}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Profile strength</span>
                    <span>40%</span>
                  </div>
                  <Progress value={40} className="h-2" />
                </div>

                <div className="glass rounded-xl p-4 space-y-2">
                  <p className="text-sm font-medium">Complete these inside the app:</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {isFounder ? (
                      <>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary shrink-0" /> Email + basic info</li>
                        <li className="flex items-center gap-2 opacity-50"><span className="w-3.5 h-3.5 rounded-full border border-muted-foreground shrink-0" /> Pitch deck or one-liner</li>
                        <li className="flex items-center gap-2 opacity-50"><span className="w-3.5 h-3.5 rounded-full border border-muted-foreground shrink-0" /> Key metrics (MRR, users)</li>
                        <li className="flex items-center gap-2 opacity-50"><span className="w-3.5 h-3.5 rounded-full border border-muted-foreground shrink-0" /> Short pitch video (optional)</li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-primary shrink-0" /> Email + basic info</li>
                        <li className="flex items-center gap-2 opacity-50"><span className="w-3.5 h-3.5 rounded-full border border-muted-foreground shrink-0" /> Investment thesis</li>
                        <li className="flex items-center gap-2 opacity-50"><span className="w-3.5 h-3.5 rounded-full border border-muted-foreground shrink-0" /> Ticket size range</li>
                        <li className="flex items-center gap-2 opacity-50"><span className="w-3.5 h-3.5 rounded-full border border-muted-foreground shrink-0" /> Portfolio companies (optional)</li>
                      </>
                    )}
                  </ul>
                </div>

                {error && <p className="text-destructive text-sm">{error}</p>}

                <Button
                  size="lg"
                  className="w-full glow-primary gap-2"
                  onClick={handleFinalSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Setting up your account…</>
                  ) : (
                    <>Start exploring <ArrowRight className="w-4 h-4" /></>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={loading}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  I'll complete this later
                </button>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default ProgressiveSignupForm;
